import {
  Component,
  OnInit,
  signal,
  inject,
  computed,
  input,
  output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormControl
} from '@angular/forms';
import { ToastService } from '@view/shared/molecules/toast/service/toast.service';
import { ToastTypeEnum } from '@view/shared/molecules/toast/toast-type-enum';
import { AddSamplePresenter } from '../../../../presenters/add-sample.presenter';
import { DropdownItem } from '@view/shared/molecules/dropdown/dropdown.component';
import { FormDropdownComponent } from '@view/shared/molecules/form-dropdown/form-dropdown.component';
import { mapToDropdownItems } from '../../shared/utils/sample-form.utils';
import { ReprocessPayload } from '@model/patient/sample-entry.model';

@Component({
  selector: 'app-reprocess-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormDropdownComponent],
  templateUrl: './reprocess-form.component.html',
  styleUrl: './reprocess-form.component.scss'
})
export class ReprocessFormComponent implements OnInit {
  readonly specimenCode = input.required<string>();
  readonly saveSuccess = output();
  readonly cancelClick = output();

  private readonly fb = inject(FormBuilder);
  private readonly toastService = inject(ToastService);
  private readonly presenter = inject(AddSamplePresenter);

  isLoading = signal(false);
  isSaving = signal(false);
  reprocessForm!: FormGroup;
  private masterDataLoaded = false;

  causeReprocessOptions = signal<DropdownItem[]>([]);
  statusReprocessOptions = signal<DropdownItem[]>([]);

  causeReprocessDropdownOptions = computed(() => this.causeReprocessOptions());
  statusReprocessDropdownOptions = computed(() =>
    this.statusReprocessOptions()
  );

  get hasReprocessControl(): FormControl {
    return this.reprocessForm.get('hasReprocess') as FormControl;
  }

  get causeReprocessIdControl(): FormControl {
    return this.reprocessForm.get('causeReprocessId') as FormControl;
  }

  get statusReprocessIdControl(): FormControl {
    return this.reprocessForm.get('statusReprocessId') as FormControl;
  }

  get observationControl(): FormControl {
    return this.reprocessForm.get('observation') as FormControl;
  }

  isReprocessEnabled(): boolean {
    return this.reprocessForm?.get('hasReprocess')?.value === true;
  }

  ngOnInit(): void {
    this.initForm();
    this.loadMasterDataIfNeeded();
    this.setupReprocessToggle();
  }

  private initForm(): void {
    this.reprocessForm = this.fb.group({
      hasReprocess: [true],
      causeReprocessId: ['', Validators.required],
      statusReprocessId: ['', Validators.required],
      observation: ['']
    });
  }

  private loadMasterDataIfNeeded(): void {
    if (this.masterDataLoaded) {
      return;
    }

    this.isLoading.set(true);
    this.presenter.loadAllMasterData().subscribe({
      next: (data) => {
        this.causeReprocessOptions.set(
          mapToDropdownItems(data.reprocessCauses)
        );
        this.statusReprocessOptions.set(
          mapToDropdownItems(data.reprocessStatuses)
        );
        this.masterDataLoaded = true;
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.toastService.setToast({
          title: 'Error al cargar datos',
          message: 'No se pudieron cargar las opciones de reproceso',
          type: ToastTypeEnum.ERROR
        });
      }
    });
  }

  private setupReprocessToggle(): void {
    this.reprocessForm.get('hasReprocess')?.valueChanges.subscribe((value) => {
      const causeControl = this.reprocessForm.get('causeReprocessId');
      const statusControl = this.reprocessForm.get('statusReprocessId');

      if (value) {
        causeControl?.setValidators([Validators.required]);
        statusControl?.setValidators([Validators.required]);
      } else {
        causeControl?.clearValidators();
        statusControl?.clearValidators();
        causeControl?.setValue('');
        statusControl?.setValue('');
        this.reprocessForm.get('observation')?.setValue('');
      }

      causeControl?.updateValueAndValidity();
      statusControl?.updateValueAndValidity();
    });
  }

  onSave(): void {
    if (!this.isReprocessEnabled()) {
      this.toastService.setToast({
        title: 'Reproceso deshabilitado',
        message: 'Active el toggle de reproceso para guardar',
        type: ToastTypeEnum.WARNING
      });
      return;
    }

    if (this.reprocessForm.invalid) {
      this.reprocessForm.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    const payload = this.buildPayload();

    this.presenter.saveReprocess(this.specimenCode(), payload).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.toastService.setToast({
          title: 'Reproceso guardado',
          message: 'El reproceso se ha registrado correctamente',
          type: ToastTypeEnum.SUCCESS
        });
        this.saveSuccess.emit();
      },
      error: (error: any) => {
        this.isSaving.set(false);
        this.toastService.setToast({
          title: 'Error al guardar',
          message:
            error?.error?.message ||
            'No se pudo guardar el reproceso. Intente nuevamente.',
          type: ToastTypeEnum.ERROR
        });
      }
    });
  }

  private buildPayload(): ReprocessPayload {
    const formValue = this.reprocessForm.getRawValue();
    return {
      reprocess: true,
      reprocessCauseId: formValue.causeReprocessId,
      reprocessStatusId: formValue.statusReprocessId,
      observation: formValue.observation || ''
    };
  }

  onCancel(): void {
    this.cancelClick.emit();
  }
}
